import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { CEE_OPERATIONS } from '~/lib/cee-data.server'
import { ArrowLeft, ArrowRight, Clock, CheckCircle, BookOpen } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const { CEE_GUIDES, GUIDE_CATEGORY_LABELS } = await import('~/lib/cee-guides-data.server')
  const guide = CEE_GUIDES.find((g) => g.slug === params.slug)
  if (!guide) throw new Response('Not Found', { status: 404 })

  const relatedOps = guide.relatedOperations
    .map((slug) => CEE_OPERATIONS.find((o) => o.slug === slug))
    .filter((o): o is NonNullable<typeof o> => !!o)

  return { guide, categoryLabel: GUIDE_CATEGORY_LABELS[guide.category], relatedOps }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const g = data.guide
  return [
    { title: `${g.title} | AfficheServices` },
    { name: 'description', content: g.description },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/cee/guides/${g.slug}` },
  ]
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

export default function CEEGuidePage() {
  const { guide, categoryLabel, relatedOps } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'CEE', url: 'https://www.afficheservices.fr/cee' },
        { name: 'Guides', url: 'https://www.afficheservices.fr/cee/guides' },
        { name: guide.shortTitle, url: `https://www.afficheservices.fr/cee/guides/${guide.slug}` },
      ]} />

      <section className="bg-gradient-to-br from-indigo-900 via-blue-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-3xl">
          <Link to="/cee/guides" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tous les guides
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-blue-200">
            <BookOpen className="h-3 w-3" /> {categoryLabel}
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">{guide.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-blue-200">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {guide.readingMinutes} min
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        {/* Intro */}
        <p className="text-base leading-relaxed text-slate-700">{renderInline(guide.intro)}</p>

        {/* Key points card */}
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-sm font-bold text-emerald-900">
            <CheckCircle className="h-4 w-4" /> À retenir
          </h2>
          <ul className="mt-3 space-y-2">
            {guide.keyPoints.map((kp, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                {kp}
              </li>
            ))}
          </ul>
        </div>

        {/* Sections */}
        <div className="mt-10 space-y-10">
          {guide.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-xl font-bold text-slate-900">{s.heading}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{renderInline(s.body)}</p>
            </section>
          ))}
        </div>

        {/* Related operations */}
        {relatedOps.length > 0 && (
          <section className="mt-12 rounded-2xl border border-border bg-slate-50 p-6">
            <h2 className="text-sm font-bold text-slate-900">Opérations CEE concernées</h2>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {relatedOps.map((op) => (
                <Link
                  key={op.slug}
                  to={`/cee/${op.slug}`}
                  className="flex items-start justify-between gap-2 rounded-xl bg-white p-3 ring-1 ring-slate-200 hover:ring-primary"
                >
                  <div>
                    <p className="text-xs font-mono text-slate-400">{op.code}</p>
                    <p className="text-sm font-semibold text-slate-900">{op.name}</p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-blue-50 to-indigo-50 p-6 text-center">
          <h2 className="text-lg font-bold text-slate-900">Prêt à lancer votre projet ?</h2>
          <p className="mt-1 text-sm text-slate-600">
            Comparez 3 devis gratuits d'artisans RGE certifiés près de chez vous.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Recevoir mes devis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
